import { SelectProps } from 'antd';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useState, useEffect } from 'react';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import { useInventories } from '@/core/domain/inventory/inventories/inventory.hooks';
import { PageRequest } from '@/core/request/PageRequest';
import { useDebounce } from '@/hooks';
import {
  InventoryFilterProps,
  InventoryStatus,
} from '@/core/domain/inventory/inventories/Inventory.types';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: PageRequest;
  companyId: number;
};

const InventoryInfiniteSelectField: React.FC<Props> = ({ ...props }) => {
  const { companyId } = props;

  const [queryParams, setQueryParams] = useState<InventoryFilterProps>({
    order: 'DESC',
    ...convertParams<InventoryFilterProps>({
      companyId: 'number',
      date: 'string',
      reason: 'string',
      status: 'string',
      search: 'string',
      order: 'string',
    }),
    status: InventoryStatus.DONE,
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useInventories(queryDebounce);

  const inventories = flatInfiniteData(data);

  const costCenterOptions = inventories.map((cur) => ({
    value: cur.id,
    label: `${cur.company.name} - ${cur.date}`,
  }));

  useEffect(() => {
    if (companyId) {
      setQueryParams((prev) => ({
        ...prev,
        companyId,
      }));
    }
  }, [companyId]);

  return (
    <InfiniteSelect
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      loading={isFetching}
      options={costCenterOptions}
      showSearch
      searchValue={queryParams.companyName}
      filterOption={false}
      onSearch={(value) => setQueryParams({ ...queryParams, date: value })}
      {...props}
    />
  );
};

export default InventoryInfiniteSelectField;
